import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { GridColDef } from '@mui/x-data-grid-pro';
import { GridSelectionModel } from '@mui/x-data-grid/models/gridSelectionModel';
import { useDispatch } from 'react-redux';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { ShadowDivider } from '@shared/styledComponents';

import { useTable } from '@shared/hooks/useTable';
import { Wrapper } from '@styles/CommonStyles';
import { selectCurrentState, updateCurrentState } from '@store/reducers/screenStateSlice';
import { useAppSelector } from '@store/hooks';
import { PhoenixGridHeader } from '../../Tools/LenderUtility/shared/PhoenixGridHeader';
import { ParentStepModal } from './components/ParentStepModal/ParentStepModal';
import { StepModal } from './components/StepModal/StepModal';
import WorkflowLevelBlock from './components/WorkflowLevelBlock/WorkflowLevelBlock';
import { WorkflowModal } from './components/WorkflowModal/WorkflowModal';
import {
  handleEditParentStep,
  requestAddNewWorkflow,
  requestAddStep,
  requestDeleteWorkflow,
  requestEditStep,
  requestRenameWorkflow,
} from './requests';
import { IWorkflow, IWorkflowStep, TParentStepFormState, TWorkflowFormState } from './types';
import { sortSteps } from './utils';

const gridName = 'workflows';

export const Workflows = () => {
  const { data: fetchedWorkflows = [], mutate, isValidating } = useSWR<IWorkflow[]>('/workflow-service/workflows/tree');

  const dispatch = useDispatch();
  const selectedState = useAppSelector(selectCurrentState(gridName));
  
  const [selectedWorkflow, setSelectedWorkflow] = useState<IWorkflow | null>(selectedState);
  const [selectedStep, setSelectedStep] = useState<IWorkflowStep | null>(null);
  const [currentStepsList, setCurrentStepsList] = useState<IWorkflowStep[]>([]);

  const [workflowModalOpenStatus, handleWorkflowModalOpenStatus] = useState(0);
  const [isEditModeWorkflowModal, setIsEditModeWorkflowModal] = useState(false);

  const [stepModalOpenStatus, handleStepModalOpenStatus] = useState(0);
  const [isEditModeStepModal, setIsEditModeStepModal] = useState(true);

  const [parentStepModalOpenStatus, handleParentStepModalOpenStatus] = useState(0);
  const [workflowLevel, setWorkflowLevel] = useState(false);
  const { sortModel, setSortModel } = useTable({ sortModel: [{ field: 'workflowName', sort: 'asc' }] });

  const sortedWorkflows = useMemo(() => [...fetchedWorkflows].sort((a, b) => a.workflowName.localeCompare(b.workflowName)), [fetchedWorkflows]);

  useEffect(() => {
    if (selectedWorkflow && selectedWorkflow?.workflowSteps?.length) {
      setCurrentStepsList(sortSteps(selectedWorkflow.workflowSteps));
    } else {
      setCurrentStepsList([]);
    }
  }, [selectedWorkflow?.workflowId, selectedWorkflow]);

  useEffect(() => {
    if (fetchedWorkflows.length > 0) {
      setSelectedWorkflow(
        fetchedWorkflows.find((workflow: IWorkflow) => workflow.workflowId === selectedWorkflow?.workflowId) || null
      );
    }
  }, [fetchedWorkflows]);

  useEffect(() => {
    if (!stepModalOpenStatus) {
      setIsEditModeStepModal(false);
      setSelectedStep(null);
    }
  }, [stepModalOpenStatus]);

  const handleAddWorkflowButtonClick = () => {
    setIsEditModeWorkflowModal(false);
    handleWorkflowModalOpenStatus(prevState => prevState + 1);
  };

  const handleWorkflowSelection = useCallback((id: GridSelectionModel) => {
    const idStr = String(id);
    if (idStr) {
      const foundWorkflow = fetchedWorkflows?.find((item) => item.workflowId === idStr) || null;
      setSelectedWorkflow(foundWorkflow);
      dispatch(updateCurrentState({ screenName: gridName, screenState: foundWorkflow }));
    }
  }, [fetchedWorkflows]);

  const handleSubmitStepForm = useCallback(
    (data: IWorkflowStep) => {
      let request;
      if (isEditModeStepModal) {
        request = requestEditStep(selectedStep?.id || '', data);
      } else {
        request = requestAddStep({
          ...data,
          workflowId: selectedWorkflow?.workflowId || null,
          parentWorkflowStepId: data.parentWorkflowStepId !== 'root' ? data.parentWorkflowStepId : null,
        });
      }
      return request.then(() => {
        handleStepModalOpenStatus(0);
        mutate();
      });
    },
    [isEditModeStepModal, selectedWorkflow, selectedStep]
  );

  const handleSubmitWorkflowForm = (data: TWorkflowFormState) => {
    let request;
    if (isEditModeWorkflowModal) {
      request = requestRenameWorkflow(selectedWorkflow?.workflowId || '', data.name);
    } else {
      request = requestAddNewWorkflow(data);
    }
    return request.then(() => {
      setIsEditModeWorkflowModal(false);
      handleWorkflowModalOpenStatus(0);
      mutate();
    });
  };

  const handleCloseWorkflowModal = () => {
    setIsEditModeWorkflowModal(false);
    handleWorkflowModalOpenStatus(0);
  };

  const handleCloseStepModal = () => {
    setIsEditModeStepModal(false);
    handleStepModalOpenStatus(0);
    setSelectedStep(null);
  };

  const handleSubmitParentStepForm = (data: TParentStepFormState) => {
    if (data?.id && data?.parentWorkflowStepId) {
      handleEditParentStep(data.id, data.parentWorkflowStepId).then(() => {
        handleParentStepModalOpenStatus(0);
        mutate();
      });
    }
  };

  const handleCloseParentStepModal = () => {
    handleParentStepModalOpenStatus(0);
  };

  const handleEditStep = (data: IWorkflowStep) => {
    setIsEditModeStepModal(true);
    handleStepModalOpenStatus(prevState => prevState + 1);
    setSelectedStep(data);
  };

  const handleAddStep = (parentStepId: string) => {
    const data = {} as IWorkflowStep;
    setSelectedStep({ ...data, parentWorkflowStepId: parentStepId });
    handleStepModalOpenStatus(prevState => prevState + 1);
  };

  const handleAddStepButtonClick = (parentStepId: string) => {
    const data = {} as IWorkflowStep;
    setSelectedStep({ ...data, parentWorkflowStepId: parentStepId });
    handleStepModalOpenStatus(prevState => prevState + 1);
  };

  const handleParentStep = (id: string | null, parentStepId: string | null) => {
    const data = {} as IWorkflowStep;
    setSelectedStep({ ...data, id: id || '', parentWorkflowStepId: parentStepId });
    handleParentStepModalOpenStatus(prevState => prevState + 1);
  };

  const workflowGridActions = {
    onEdit: (data: IWorkflow) => {
      setSelectedWorkflow(data);
      handleWorkflowModalOpenStatus(prevState => prevState + 1);
      setIsEditModeWorkflowModal(true);
    },
    onDelete: async (data: IWorkflow) => {
      await requestDeleteWorkflow(data.workflowId);
      mutate();
    },
  };
  const workflowColumns: GridColDef[] = [
    dataGridCol('workflowName', {
      flex: 2,
      minWidth: 240,
      renderCell: (params) => <Typography>{`${params?.row?.workflowName} (${params?.row?.workflowNumericId})`}</Typography>,
    }),
    dataGridCol('entityType'),
  ];

  return (
    <Wrapper>
      <PhoenixGridHeader
        title="Workflows"
        buttonTitle="Add New Workflow"
        buttonWidth="220px"
        onButtonClick={handleAddWorkflowButtonClick}
      />
      <ShadowDivider />

      <Box
        max-width="100%"
        display="flex"
        height="100%"
      >
        <Stack
          flex={3}
          marginRight={2}
          minWidth="360px"
        >
          <PhoenixGrid
            autoHeight
            canNotCopy
            disableMultipleSelection
            pageSize={15}
            name={gridName}
            columns={workflowColumns}
            loading={!fetchedWorkflows || isValidating}
            rows={fetchedWorkflows && !isValidating ? fetchedWorkflows : []}
            actions={workflowGridActions}
            sortModel={sortModel}
            getRowId={(row: IWorkflow) => row?.workflowId}
            selectionModel={selectedWorkflow ? [selectedWorkflow?.workflowId] : []}
            onSortModelChange={setSortModel}
            onSelectionModelChange={handleWorkflowSelection}
          />
        </Stack>
        <Stack
          flex={5}
          overflow="auto"
          position="relative"
        >
          {(selectedWorkflow?.workflowId && !isValidating) && (
            <WorkflowLevelBlock
              currentStepsList={currentStepsList}
              setWorkflowLevel={setWorkflowLevel}
              handleAddStep={handleAddStep}
              handleSelectStep={handleEditStep}
              handleAddStepButtonClick={handleAddStepButtonClick}
              handleEditParentStep={handleParentStep}
            />
          )}
        </Stack>
      </Box>

      {!!workflowModalOpenStatus && (
        <WorkflowModal
          currentWorkflow={selectedWorkflow}
          workflowModalPosition={workflowModalOpenStatus}
          isEditMode={isEditModeWorkflowModal}
          handleClose={handleCloseWorkflowModal}
          handleSubmit={handleSubmitWorkflowForm}
        />
      )}

      {!!stepModalOpenStatus && (
        <StepModal
          tStepModalPositon={stepModalOpenStatus}
          stepData={selectedStep}
          isEditMode={isEditModeStepModal}
          workflowLevel={workflowLevel}
          handleSubmit={handleSubmitStepForm}
          handleClose={handleCloseStepModal}
          refreshWorflow={mutate}
          workflows={sortedWorkflows}
        />
      )}

      {!!parentStepModalOpenStatus && (
        <ParentStepModal
          parentStepModalPosition={parentStepModalOpenStatus}
          currentStep={selectedStep}
          handleSubmit={handleSubmitParentStepForm}
          handleClose={handleCloseParentStepModal}
          selectedWorkflow={selectedWorkflow}
        />
      )}
    </Wrapper>
  );
};
